import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';

import useOnMobileBreakpointChange from './useOnMobileBreakpointChange';

const useNoddyBodyScrollLock = () => {
  const scrollPos = useRef(canUseDOM ? window.scrollY : 0);

  const lock = useCallback(() => {
    scrollPos.current = Math.max(
      window.scrollY,
      document.body.scrollTop,
      document.documentElement.scrollTop,
      document.scrollingElement?.scrollTop ?? 0
    );

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'relative';
    document.body.style.top = `-${scrollPos.current}px`;
    document.body.style.width = '100%';
    document.documentElement.style.height = '100vh';
  }, []);

  const unlock = useCallback(() => {
    document.documentElement.style.removeProperty('height');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
    window.scrollTo(0, scrollPos.current);
  }, []);

  return { lock, unlock };
};

const useMobileBodyScrollLock = (): boolean => {
  const isBetSlipOpen = useSelector(select.betSlip.isOpen);
  const currentModal = useSelector(select.modal.getCurrent);
  const [isBodyScrollLocked, setIsBodyScrollLocked] = useState(false);
  const { lock, unlock } = useNoddyBodyScrollLock();

  // Remove scroll lock when resizing to bigger than mobile
  const onMobileBreakpointChange = useCallback(
    (isMobile) => setIsBodyScrollLocked(!isMobile && isBetSlipOpen),
    [isBetSlipOpen]
  );

  useOnMobileBreakpointChange(onMobileBreakpointChange);

  // Remove scroll lock when the bet slip is closed
  useEffect(() => setIsBodyScrollLocked(isBetSlipOpen), [isBetSlipOpen]);

  useEffect(() => {
    if (isBodyScrollLocked) {
      lock();
    } else {
      unlock();
    }
  }, [isBodyScrollLocked, lock, unlock]);

  // Toggle locked state based on account modal
  useEffect(() => {
    if (!isBetSlipOpen) {
      return;
    }

    if (!currentModal) {
      setIsBodyScrollLocked(true);
    }

    if (currentModal?.path.startsWith('/account')) {
      setIsBodyScrollLocked(false);
    }
  }, [currentModal, isBetSlipOpen, unlock]);

  useEffect(
    () => () => {
      unlock();
    },
    [unlock]
  );

  return isBodyScrollLocked;
};

export default useMobileBodyScrollLock;
